import React, { useState, useEffect, useCallback } from "react";
import styles from "../styles/advancedConfig.module.css";
import { BACKEND_URL } from "../config";

const AdvancedConfig = ({ updateRevisions, setShowPremiumModal, setQuotaErrorMessage }) => {
    const [formData, setFormData] = useState({
        complexCurveName: "",
        paramK: 80, // Default value (80%)
        paramC2: "moderate", // Default value (moderate difficulty)
    });
    const [syncGoogleCalendar, setSyncGoogleCalendar] = useState(false);
    const [loading, setLoading] = useState(false);

    // Options pour les sliders
    const c2Options = ["minimal", "low", "moderate", "high", "intensive"];
    const c2ValueMap = { minimal: 2.5, low: 2.25, moderate: 2, high: 1.75, intensive: 1.5 };
    
    // Mapping automatique entre C2 et C1 (en heures)
    const c1ValueMap = { minimal: 120, low: 96, moderate: 72, high: 48, intensive: 24 };

    const sendMemoryCurve = useCallback(async (payload) => {
        setLoading(true);
        
        try {
            console.log("Making request with payload:", JSON.stringify(payload));
            
            const response = await fetch(`${BACKEND_URL}/add-memory-curve`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            console.log("Response status:", response.status, response.statusText);
            console.log("Response headers:", Object.fromEntries([...response.headers]));
            
            // Log the raw response for debugging
            const responseText = await response.text();
            console.log("Raw response:", responseText);
            
            let data;
            try {
                // Parse the response text back to JSON only if it's not empty
                data = responseText ? JSON.parse(responseText) : {};
            } catch (e) {
                console.error("Failed to parse response JSON:", e);
                alert("Server returned an invalid response. Please try again.");
                setLoading(false);
                return;
            }

            if (response.ok) {
                // Vérifier si la courbe a été ajoutée mais l'autorisation du calendrier est nécessaire
                if (data.needs_calendar_auth) {
                    alert("Memory curve added successfully, but Google Calendar authorization is required.");
                    
                    try {
                        // Stocker les infos de la courbe pendant la redirection
                        sessionStorage.setItem("calendar_auth_pending", "true");
                        
                        // Rediriger vers l'URL d'autorisation du calendrier
                        const userId = sessionStorage.getItem("user_id");
                        const calendarAuthResponse = await fetch(`${BACKEND_URL}/oauth/calendar?user_id=${encodeURIComponent(userId)}`);
                        
                        if (!calendarAuthResponse.ok) {
                            const errorData = await calendarAuthResponse.text();
                            console.error("Failed to get calendar auth URL:", errorData);
                            alert("Failed to initiate Google Calendar authorization. Please try again later.");
                            return;
                        }
                        
                        const calendarAuthData = await calendarAuthResponse.json();
                        
                        if (calendarAuthData.url) {
                            window.location.href = calendarAuthData.url;
                            return; // Arrêter ici car l'utilisateur est redirigé
                        } else {
                            console.error("No URL in calendar auth response:", calendarAuthData);
                            alert("Failed to initiate Google Calendar authorization. Please try again later.");
                        }
                    } catch (error) {
                        console.error("Error during calendar authorization redirect:", error);
                        alert("An error occurred during Google Calendar authorization. Please try again later.");
                    }
                } else {
                    alert("Advanced memory curve added successfully!");
                }
                
                // Réinitialiser le formulaire avec les valeurs par défaut
                setFormData({ 
                    complexCurveName: "", 
                    paramK: 80, 
                    paramC2: "moderate" 
                });

                if (updateRevisions) {
                    updateRevisions();  // Met à jour la liste des révisions sans recharger toute la page
                } else {
                    window.location.reload();  // Recharge la page si `updateRevisions` n'est pas défini
                }
            } else {
                console.error("Error response:", data);
                // Vérifier si c'est une erreur de quota (403)
                if (response.status === 403 && data.detail && data.detail.includes("Maximum number of memory curves reached")) {
                    // Capturer et afficher le message d'erreur via les props
                    setQuotaErrorMessage(data.detail);
                    setShowPremiumModal(true);
                } else if (data && data.detail) {
                    if (typeof data.detail === 'object') {
                        // Pour les erreurs de validation FastAPI qui retournent un objet
                        alert(`Validation error: ${JSON.stringify(data.detail)}`);
                    } else {
                        alert(`Error: ${data.detail}`);
                    }
                } else {
                    alert(`Failed to add advanced memory curve (${response.status}). Check console for details.`);
                }
            }
        } catch (error) {
            console.error("Error adding memory curve:", error);
            alert(`An error occurred: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [updateRevisions, setQuotaErrorMessage, setShowPremiumModal]);

    useEffect(() => {
        // Vérifie si une courbe est en attente après la connexion Google OAuth
        const pendingCurve = sessionStorage.getItem("pendingCurve");
        if (pendingCurve) {
            sendMemoryCurve(JSON.parse(pendingCurve));
            sessionStorage.removeItem("pendingCurve");
        }
    }, [sendMemoryCurve]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.id]: e.target.value });
    };

    const checkGoogleConnection = async () => {
        const userId = sessionStorage.getItem("user_id");

        if (!userId) {
            console.error("User ID is missing from session storage.");
            return false;
        }

        try {
            const response = await fetch(`${BACKEND_URL}/check-google-token?user_id=${encodeURIComponent(userId)}`);
            if (!response.ok) {
                console.error("Error response from Google token check:", await response.text());
                return false;
            }
            const data = await response.json();
            return data.connected;
        } catch (error) {
            console.error("Error checking Google connection:", error);
            return false;
        }
    };

    const addAdvancedCurve = async () => {
        const userId = sessionStorage.getItem("user_id");
    
        if (!userId) {
            alert("Please log in first.");
            console.error("User ID is missing from session storage.");
            return;
        }
    
        if (!formData.complexCurveName) {
            alert("Please provide a revision name.");
            return;
        }
    
        // Conversion des valeurs des sliders en paramètres pour l'API
        const payload = {
            user_id: userId,
            revision_name: formData.complexCurveName,
            K: parseFloat(formData.paramK) / 100, // Conversion du pourcentage
            C1: c1ValueMap[formData.paramC2] * 3600, // Conversion des heures en secondes selon la difficulté
            C2: c2ValueMap[formData.paramC2], // Conversion de la difficulté en valeur numérique
            step: 100, // Valeur par défaut, même si elle sera remplacée côté serveur
            synchroGoogleCalendar: syncGoogleCalendar,
        };
    
        console.log("Sending payload:", JSON.stringify(payload, null, 2));
        setLoading(true);
    
        if (syncGoogleCalendar) {
            try {
                console.log("Checking Google connection...");
                const googleConnected = await checkGoogleConnection();
                if (!googleConnected) {
                    console.log("Redirecting to Google OAuth...");
                    sessionStorage.setItem("pendingCurve", JSON.stringify(payload));
                    
                    // Redirection immédiate vers l'URL OAuth Google en utilisant la nouvelle route pour le calendrier
                    const response = await fetch(`${BACKEND_URL}/oauth/calendar?user_id=${encodeURIComponent(userId)}`);
                    const data = await response.json();
                    
                    if (data.url) {
                        window.location.href = data.url;  //  Redirection immédiate vers la page Google OAuth
                    } else {
                        alert("Error connecting to Google.");
                    }
                    return;  //  Stopper ici car l'utilisateur doit d'abord se connecter
                }
            } catch (error) {
                console.error("Error during Google OAuth request:", error);
            }
        }
    
        // Ajouter la mémoire courbe immédiatement si OAuth n'est pas activé
        await sendMemoryCurve(payload);
    };
    

    return (
        <div className={styles.tabContent}>
            <h3>Advanced Configuration </h3>

            <label className={styles.label}>Revision Name:</label>
            <input className={styles.input} type="text" id="complexCurveName" value={formData.complexCurveName} onChange={handleChange} required />

            <label className={styles.label}>What is the minimum retention level you want to maintain?</label>
            <div className={styles.inputContainer}>
                <div className={styles.sliderContainer}>
                    <input 
                        type="range" 
                        id="paramK" 
                        min="10" 
                        max="90" 
                        step="1" 
                        value={formData.paramK} 
                        onChange={handleChange} 
                        className={styles.slider} 
                    />
                    <div className={styles.sliderValue}>{formData.paramK}%</div>
                </div>
                <div className={styles.helpIcon}>?
                    <div className={styles.tooltip}>
                        Le facteur K détermine la vitesse de progression de la courbe de mémorisation.
                    </div>
                </div>
            </div>

            <label className={styles.label}>How difficult is the content to memorize for you?</label>
            <div className={styles.inputContainer}>
                <div className={styles.sliderContainer}>
                    <div className={styles.discreteSliderContainer}>
                        <input 
                            type="range" 
                            min="0" 
                            max="4" 
                            step="1"
                            value={c2Options.indexOf(formData.paramC2)}
                            onChange={(e) => {
                                setFormData({
                                    ...formData,
                                    paramC2: c2Options[e.target.value]
                                });
                            }}
                            className={styles.slider}
                        />
                        <div className={styles.tickMarksContainer}>
                            {c2Options.map((value, index) => (
                                <div 
                                    key={index} 
                                    className={`${styles.tickMark} ${formData.paramC2 === value ? styles.activeTick : ''}`}
                                    onClick={() => setFormData({...formData, paramC2: value})}
                                >
                                    <span className={styles.tickLabel}>{value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className={styles.helpIcon}>?
                    <div className={styles.tooltip}>
                        Le niveau de difficulté influence à la fois C2 (facteur d'ajustement de la courbe) et 
                        C1 (délai avant la première révision).
                    </div>
                </div>
            </div>

            <div className={styles.toggleCalendar}>
                <img src="/gCalendar.png" alt="Google Calendar" className={styles.calendarIcon} />
                <p>Synchronize with Google Calendar</p>
                <label className={styles.switch}>
                    <input type="checkbox" checked={syncGoogleCalendar} onChange={() => setSyncGoogleCalendar((prev) => !prev)} />
                    <span className={styles.toggleSlider}></span>
                </label>
            </div>

            <button className={styles.addButton} onClick={addAdvancedCurve} disabled={loading}>
                {loading ? "Adding..." : "Add"}
            </button>
        </div>
    );
};

export default AdvancedConfig;
